import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TOPIC_TAGS } from '../../lib/constants';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { Dialog } from '../../ui/Dialog';
import { loc, type Loc } from '../../lib/loc';
import { EvidenceHint, type Evidence } from '../r3/common';
import { errorText, rpc, type AppError, type SessionResults } from './api';

type Item = { key: string; topic: string; title: Loc; body: Loc; is_draft: boolean; school_version: boolean; evidence?: Evidence };
type Action = { id: string; kind: string; playbook_key: string | null; topic: string | null; created_at: string };
type CReq = { id: string; topics: string[]; status: string; created_at: string };
const FIXED = ['class_discussion', 'adjusted_workload', 'other'] as const;

// I3: suggested activities for the main topics, the action log ("What I
// did"), and a class-level counsellor visit request.
export function ActOnResults({ data }: { data: SessionResults }) {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const [items, setItems] = useState<Item[] | null>(null);
  const [actions, setActions] = useState<Action[]>([]);
  const [reqs, setReqs] = useState<CReq[]>([]);
  const [err, setErr] = useState<AppError | null>(null);
  const [asking, setAsking] = useState(false);
  const [pick, setPick] = useState<string[]>([]);
  const r = data.results;
  const sid = data.session.id;
  const shownTopics = r.status === 'ok'
    ? Object.entries(r.topic_counts).filter(([, n]) => n !== null && n > 0).sort((a, b) => (b[1] ?? 0) - (a[1] ?? 0)).map(([k]) => k)
    : [];
  const main = shownTopics.slice(0, 2);
  const mainKey = main.join(',');

  const load = useCallback(async () => {
    const [a, c] = await Promise.all([
      rpc<Action[]>('teacher_session_actions', { p_session_id: sid }),
      rpc<CReq[]>('teacher_counsellor_requests', { p_session_id: sid }),
    ]);
    if (!a.error) setActions(a.data);
    if (!c.error) setReqs(c.data);
    if (mainKey) {
      const p = await rpc<{ items: Item[] }>('teacher_playbook', { p_topics: mainKey.split(',') });
      if (p.error) setErr(p.error);
      else setItems(p.data.items);
    } else setItems([]);
  }, [sid, mainKey]);
  useEffect(() => {
    load();
  }, [load]);

  async function log(kind: string, key?: string) {
    setErr(null);
    const x = await rpc('teacher_log_action', { p_session_id: sid, p_kind: kind, p_playbook_key: key ?? null });
    if (x.error) return setErr(x.error);
    announce(t['act.logged']);
    load();
  }
  async function remove(id: string) {
    const x = await rpc('teacher_remove_action', { p_action_id: id });
    if (x.error) return setErr(x.error);
    load();
  }
  async function ask(e?: FormEvent) {
    e?.preventDefault();
    const x = await rpc('teacher_request_counsellor', { p_session_id: sid, p_topics: pick });
    if (x.error) return setErr(x.error);
    setAsking(false);
    announce(t['act.asked']);
    load();
  }

  const titleOf = (a: Action) => (a.kind === 'playbook' ? loc(items?.find((i) => i.key === a.playbook_key)?.title, lang) || a.playbook_key : t[`act.${a.kind}` as 'act.other']);
  const openReq = reqs.find((q) => q.status === 'open' || q.status === 'acknowledged');

  return (
    <section className="panel p5" aria-labelledby="act-title">
      <h2 className="panel-title" id="act-title">{t['act.title']}</h2>
      {r.status !== 'ok' ? (
        <p className="text-muted">{t['act.needResults']}</p>
      ) : (
        <>
          {main.length > 0 && <p>{t['act.mainTopics'](main.map((m) => t[`topic.${m}` as 'topic.exams']).join(', '))}</p>}
          {items && items.length > 0 && (
            <ul className="pb-list">
              {items.filter((i) => i.key !== 'any_counsellor_visit').map((i) => (
                <li key={i.key}>
                  <p className="pb-title"><strong>{loc(i.title, lang)}</strong> {i.is_draft && <Pill kind="warn">{t['ts.draft']}</Pill>}</p>
                  <p>{loc(i.body, lang)}</p>
                  <EvidenceHint e={i.evidence} />
                  <button type="button" className="link-btn" onClick={() => log('playbook', i.key)}>
                    {t['act.didThis']}<span className="visually-hidden"> – {loc(i.title, lang)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="field-label">{t['act.orOther']}</p>
          <div className="chip-row">
            {FIXED.map((k) => (
              <button key={k} type="button" className="btn btn-ghost" onClick={() => log(k)}>{t[`act.${k}` as 'act.other']}</button>
            ))}
          </div>
          <h3 className="overline">{t['act.counsellorTitle']}</h3>
          {openReq ? (
            <p>{t['act.reqStatus'](t[`co.st.${openReq.status}` as 'co.st.open'])}</p>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={() => { setPick(main); setAsking(true); }}>{t['act.ask']}</button>
          )}
        </>
      )}
      {actions.length > 0 && (
        <>
          <h3 className="overline">{t['act.done']}</h3>
          <ul className="sched-list">
            {actions.map((a) => (
              <li key={a.id}>
                <span>{titleOf(a)}</span>
                {a.kind !== 'counsellor_visit' && <button type="button" className="link-btn" onClick={() => remove(a.id)}>{t['ts.schedEnd']}<span className="visually-hidden"> – {titleOf(a)}</span></button>}
              </li>
            ))}
          </ul>
        </>
      )}
      <p className="form-hint">{t['act.who']}</p>
      <p><Link className="ts-link" to="/teacher/playbook">{t['act.allPlaybook']}</Link></p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      <Dialog open={asking} title={t['act.askTitle']} intro={<p>{t['act.askIntro']}</p>} submitLabel={t['act.askSend']}
        onSubmit={ask} onClose={() => setAsking(false)} wide>
        <fieldset className="fieldset">
          <legend className="field-label">{t['act.askTopics']}</legend>
          <div className="chip-row">
            {TOPIC_TAGS.filter((tg) => shownTopics.includes(tg.value)).map((tg) => {
              const on = pick.includes(tg.value);
              return (
                <label key={tg.value} className={`chip-check${on ? ' on' : ''}`}>
                  <input type="checkbox" checked={on} onChange={() => setPick((p) => (on ? p.filter((x) => x !== tg.value) : [...p, tg.value]))} />
                  <span aria-hidden="true">{tg.emoji}</span> {t[`topic.${tg.value}` as 'topic.exams']}
                </label>
              );
            })}
          </div>
        </fieldset>
      </Dialog>
    </section>
  );
}
